import { useState } from "react";
import { Button } from "./ui/button";
import { EnvelopeClosedIcon } from "@radix-ui/react-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/utils/api";
import { API_ENDPOINTS } from "@/config/api";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useToast } from "./ui/use-toast";

interface InviteDialogProps {
  assessmentId: string;
  onInvite?: () => void; // Optional callback after successful invite
}

export function InviteDialog({ assessmentId, onInvite }: InviteDialogProps) {
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const navigateToCandidatesTab = () => {
    // Navigate to candidates tab if not already there
    if (searchParams.get("tab") !== "candidates") {
      navigate(`/assessments/${uuid}?tab=candidates`, { replace: true });
    }
  };

  const handleSubmit = async () => {
    setError("");
    setIsLoading(true);

    try {
      const emailList = emails
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);

      if (!emailList.length) {
        setError("Please enter at least one email address");
        setIsLoading(false);
        return;
      }

      await apiRequest(API_ENDPOINTS.assessments.invite(assessmentId), {
        method: "POST",
        body: { emails: emailList },
      });

      setOpen(false);
      setEmails("");

      // Navigate to candidates tab
      navigateToCandidatesTab();

      // Show success toast
      toast({
        title: "Invites sent",
        description: `Successfully sent invites to ${emailList.length} candidate(s)`,
        variant: "default",
      });

      // Call the onInvite callback if provided
      if (onInvite) {
        onInvite();
      }
    } catch (err: any) {
      // Check if the error has existingEmails property
      if (err.existingEmails && err.existingEmails.length > 0) {
        const existingEmailsStr = err.existingEmails.join(", ");
        setError(
          `The following emails are already invited: ${existingEmailsStr}`
        );
      } else {
        setError(err.message || "An error occurred while sending invites");
      }

      toast({
        title: "Error",
        description: err.message || "An error occurred while sending invites",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen && !isLoading && onInvite) {
          // When dialog is closed and not in loading state
          navigateToCandidatesTab();
          onInvite();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button icon={<EnvelopeClosedIcon className="h-4 w-4" />}>Invite</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite candidates</DialogTitle>
          <DialogDescription>
            Enter email addresses separated by commas of the candidates you wish
            to invite.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="emails">Email addresses</Label>
            <Input
              id="emails"
              placeholder="john@example.com, jane@example.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit} isLoading={isLoading}>
            Send invites
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
